import type { Gender, PersonNameDto } from '../../config/contactConfig'
import type { LocalizedValueSetting } from '../../context/GlobalContext'

/**
 * Minimal shape needed to build a salutation / attention-of string. Both the natural-person
 * mutation DTO and any plain test fixture satisfy this, so the helper stays a pure function
 * without depending on the full form DTO.
 */
export type SalutationSource = {
    gender?: Gender
    name?: Partial<PersonNameDto>
}

export type SalutationSetting = LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]

/**
 * Builds the default salutation ("aanhef") or attention-of ("t.a.v.") string from a tenant setting,
 * resolved per language and per gender, with the name placeholders substituted.
 *
 * Placeholders: ${gender} ${initials} ${first} ${given} ${insertion} ${insertion|upper} ${last}.
 * Double (or more) whitespace is collapsed and the result trimmed, so absent name parts don't
 * leave gaps. Pure: no i18n/React dependency (takes the language code directly), which keeps it
 * unit-testable per the client test constraint.
 */
export function getDefaultSettingValue(person: SalutationSource, language: string, setting: SalutationSetting): string {
    let settingValue = setting?.find(entry => entry.key === language)?.value?.find(entry => entry.key === person.gender)?.value || ''

    let insertionUpper: string = person.name?.insertion
    if (insertionUpper?.length > 1) {
        insertionUpper = insertionUpper.charAt(0).toUpperCase() + insertionUpper.slice(1)
    } else if (insertionUpper?.length === 1) {
        insertionUpper = insertionUpper.toUpperCase().toWellFormed()
    }

    settingValue = settingValue.replaceAll('${gender}', person.gender || '')
    settingValue = settingValue.replaceAll('${initials}', person.name?.initials || '')
    settingValue = settingValue.replaceAll('${first}', person.name?.first || '')
    settingValue = settingValue.replaceAll('${given}', person.name?.given || '')
    settingValue = settingValue.replaceAll('${insertion|upper}', insertionUpper || '')
    settingValue = settingValue.replaceAll('${insertion}', person.name?.insertion || '')
    settingValue = settingValue.replaceAll('${last}', person.name?.last || '')

    settingValue = settingValue.replaceAll(/\s{2,}/gm, ' ')
    return settingValue.trim()
}
